import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Strip HTML tags and known XSS vectors from a string before sending to the server.
 * React already escapes values in JSX, but sanitizing before storage prevents
 * downstream issues if the data is ever consumed outside React.
 */
export function sanitizeText(input: string): string {
  return input
    .replace(/<[^>]*>/g, '') // strip HTML tags
    .replace(/javascript:/gi, '') // strip js: URIs
    .replace(/on\w+\s*=/gi, '') // strip inline event handlers (onclick=, onerror=, …)
    .trim();
}

// ——— Org field validation ———

export const ORG_PACKAGE_NAME_MAX = 100;
export const ORG_NAME_MAX = 50;
export const ORG_NAME_MIN = 2;
export const ORG_SLUG_MAX = 30;
export const ORG_SLUG_MIN = 2;

/**
 * Validate org display name.
 * Allows letters (including unicode), numbers, spaces, hyphens, underscores,
 * apostrophes, and periods. No angle brackets, script tags, or raw HTML.
 */
export function validateOrgName(value: string): string | null {
  const v = value.trim();
  if (v.length < ORG_NAME_MIN)
    return `Name must be at least ${ORG_NAME_MIN} characters.`;
  if (v.length > ORG_NAME_MAX)
    return `Name must be ${ORG_NAME_MAX} characters or fewer.`;
  if (/[<>"&]/.test(v)) return 'Name must not contain < > " & characters.';
  if (/^\s|\s$/.test(value))
    return 'Name must not have leading or trailing spaces.';
  return null;
}

/**
 * Validate package name for org linking.
 * Allows letters, numbers, dots, hyphens, underscores.
 * Must start with a letter or number. No consecutive dots.
 */
export function validateOrgPackageName(value: string): string | null {
  const v = value.trim();
  if (!v) return 'Package name is required.';
  if (v.length < 2) return 'Package name must be at least 2 characters.';
  if (v.length > ORG_PACKAGE_NAME_MAX)
    return `Package name must be ${ORG_PACKAGE_NAME_MAX} characters or fewer.`;
  if (!/^[a-zA-Z0-9][a-zA-Z0-9._-]*$/.test(v))
    return 'Only letters, numbers, dots, hyphens, and underscores. Must start with a letter or number.';
  if (/\.\./.test(v)) return 'Package name must not contain consecutive dots.';
  if (/[<>"&]/.test(v))
    return 'Package name must not contain < > " & characters.';
  return null;
}

/**
 * Validate org slug.
 * Only lowercase letters, numbers, and hyphens. Must start and end with
 * an alphanumeric character. No consecutive hyphens.
 */
export function validateOrgSlug(value: string): string | null {
  const v = value.trim();
  if (v.length < ORG_SLUG_MIN)
    return `Slug must be at least ${ORG_SLUG_MIN} characters.`;
  if (v.length > ORG_SLUG_MAX)
    return `Slug must be ${ORG_SLUG_MAX} characters or fewer.`;
  if (!/^[a-z0-9-]+$/.test(v))
    return 'Slug may only contain lowercase letters, numbers, and hyphens.';
  if (/^-|-$/.test(v))
    return 'Slug must start and end with a letter or number.';
  if (/--/.test(v)) return 'Slug must not contain consecutive hyphens.';
  return null;
}

// ——— Card formatting ———
//
// Every one of these takes a value that is legitimately absent in production
// data: three published bundles have no icon, and `installSize`/`publishedAt`
// are null for everything published before the metadata policy shipped. They
// return null rather than a placeholder string so a caller decides whether to
// render a dash, a skeleton, or nothing at all — a formatter that invents
// "0 bytes" or "Invalid Date" is confidently wrong and looks deliberate.

/** `204472` -> `"200 KB"`. Null for an unknown size. */
export function formatBytes(bytes?: number | null): string | null {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) {
    return null;
  }
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  // One decimal below 10 ("1.2 MB"), none above ("200 KB") — the extra digit
  // is noise at three figures and makes card rows ragged.
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

/** ISO timestamp -> `"2 days ago"`. Null for a missing or unparseable date. */
export function formatRelativeDate(iso?: string | null): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;

  const seconds = Math.round((Date.now() - then) / 1000);
  // A clock skew or a just-written timestamp can land microseconds in the
  // future; "in 0 seconds" is worse than "just now".
  if (seconds < 60) return 'just now';

  const steps: [number, Intl.RelativeTimeFormatUnit][] = [
    [60, 'minute'],
    [24, 'hour'],
    [7, 'day'],
    [4.345, 'week'],
    [12, 'month'],
    [Number.POSITIVE_INFINITY, 'year'],
  ];
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  let value = seconds / 60;
  for (const [divisor, unit] of steps) {
    if (Math.abs(value) < divisor) return rtf.format(-Math.round(value), unit);
    value /= divisor;
  }
  return rtf.format(-Math.round(value), 'year');
}

/** Human label for a category slug: `art-design` -> `Art & Design`. */
export function formatCategory(category?: string | null): string | null {
  if (!category) return null;
  if (category === 'art-design') return 'Art & Design';
  if (category === 'developer-tools') return 'Developer Tools';
  return category
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * A stable hue for an app with no icon, derived from its package id so the
 * same app always gets the same tint across renders and machines.
 */
export function fallbackHue(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % 360;
}

/**
 * Build provenance stamped into a bundle manifest by `cargo mero bundle`.
 * Every field is optional: it is derived from however the app's `calimero-sdk`
 * dependency happened to resolve, and not every resolution names a release.
 */
export interface BuildInfo {
  sdkSource?: string;
  sdkVersion?: string;
  sdkRev?: string;
}

/**
 * The node release a bundle was compiled against, as a label — or `null` when
 * the bundle does not say.
 *
 * ⚠️ NULL IS THE COMMON CASE AND MUST RENDER AS NOTHING. Every bundle published
 * before cargo-mero began stamping `buildInfo` has none, so a caller that
 * substitutes a placeholder is asserting a version the bundle never claimed.
 * The same page already carries a scar from this: the manifest-version card
 * rendered the literal string "vundefined" for bundles predating that field.
 *
 * Three shapes reach here, in descending order of what they pin down:
 *   - a release  -> `0.11.0-rc.34`  (a git `tag=`, or a crates.io version)
 *   - a commit   -> `6c6fb4a`       (a branch/rev build: no release to name,
 *                                    but the commit identifies it exactly)
 *   - neither    -> `null`          (a local path build, or an old bundle)
 */
export function nodeBuildLabel(build?: BuildInfo | null): string | null {
  const version = build?.sdkVersion?.trim();
  if (version) return version;

  // No release was named. The commit still identifies the build, and a short
  // SHA is what a reader can actually paste into `git show`.
  const rev = build?.sdkRev?.trim();
  if (rev) return rev.slice(0, 7);

  return null;
}
